import { isClientView } from '../common/organizationAccessMode';
import { formatLocalDate } from '../utils/localDateTime';
import React, { useEffect, useState } from 'react';
import MsaPaymentButton from './MsaPaymentButton';
import { getFunctions, httpsCallable } from 'firebase/functions';
import type { Organization, UserProfile } from '@types';
import { openCheckout } from '../../services/paddleService';
import { getOrganizationDisplayName } from '@common/organizationDisplayName';
import { subscriptionStatusLabel } from '../utils/subscriptionStatusLabel';
import { canManageClientAccount } from '../common/clientAccountAccess';

interface ReactivationQuote {
    status: string;
    accessUntil?: string;
    trialEndsAt?: string;
    tier: string;
    total: number;
    currencyCode: string;
    items: Array<{ priceId: string; quantity: number; name: string; amount: string }>;
}

interface Props {
    organization: Organization;
    currentUser: UserProfile;
    missing: boolean;
}

const money = (amount: number, currency = 'USD') => new Intl.NumberFormat('en-US', {
    style: 'currency', currency, maximumFractionDigits: 0,
}).format(amount / 100);

const date = (value?: string) => formatLocalDate(value, 'Unavailable');

const MsaAccessGate: React.FC<Props> = ({ organization, currentUser, missing }) => {
    const canManageAccount = !isClientView && canManageClientAccount(currentUser.role);
    const expiredTrial = organization.msaTrialMode === 'app' && Boolean(organization.trialEndsAt) && new Date(organization.trialEndsAt!).getTime() <= Date.now();
    const [quote, setQuote] = useState<ReactivationQuote | null>(null);
    const [loading, setLoading] = useState(!missing && !expiredTrial && canManageAccount);
    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (missing || expiredTrial || !canManageAccount)
            return;
        const callable = httpsCallable(getFunctions(undefined, 'us-central1'), 'getMsaReactivation');
        void callable({}).then(result => setQuote(result.data as ReactivationQuote)).catch(failure => {
            setError(failure.message || 'MSA details could not be loaded.');
        }).finally(() => setLoading(false));
    }, [missing, expiredTrial, canManageAccount]);

    const reactivate = async () => {
        if (!quote)
            return;
        setProcessing(true);
        setError('');
        try {
            await openCheckout({
                items: quote.items.map(item => ({ priceId: item.priceId, quantity: item.quantity })),
                customer: { email: currentUser.email },
                customData: { organization_id: organization.id, user_id: currentUser.uid, msa_reactivation: true },
                settings: { displayMode: 'overlay', theme: 'light', showAddDiscounts: true, successUrl: `${window.location.origin}/dashboard?checkout=success` },
            });
            setProcessing(false);
        } catch (failure: any) {
            setError(failure.message || 'Checkout could not start.');
            setProcessing(false);
        }
    };

    return <section className="onboarding-theme flex min-h-[70vh] items-center justify-center px-4 py-12">
        <div role="dialog" aria-modal="true" aria-labelledby="msa-access-title" className="w-full max-w-3xl overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl">
            <div className="bg-slate-50 px-8 py-9 text-slate-900">
                <p className="text-xs font-extrabold uppercase tracking-[0.25em] text-blue-700">Platform access</p>
                <h1 id="msa-access-title" className="mt-3 text-3xl font-black">{missing ? 'Begin your free trial' : expiredTrial ? 'Your free trial has ended' : 'MSA subscription inactive'}</h1>
                <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-600">{missing
                    ? `Contact your IntelOptics administrator for a free trial link for ${getOrganizationDisplayName(organization)}.`
                    : expiredTrial ? `The free trial for ${getOrganizationDisplayName(organization)} ended ${date(organization.trialEndsAt)}. ${organization.msaPaymentStatus === 'scheduled' ? 'Your first payment is being confirmed.' : 'Complete your MSA and payment setup to restore access.'}` : `The Master Service Agreement subscription for ${getOrganizationDisplayName(organization)} is currently ${subscriptionStatusLabel(organization.subscriptionStatus, organization.isTestOrganization, 'Inactive')}.`}</p>
            </div>
            <div className="space-y-6 p-8">
                {!canManageAccount && <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-900">Your organization owner or administrator must complete this billing action. Contact them to restore access.</div>}
                {canManageAccount && missing && <>
                    <p className="text-sm leading-6 text-slate-600">A unique free trial link is required. Ask your IntelOptics contact for a link to begin.</p>
                </>}
                {canManageAccount && expiredTrial && (organization.msaPaymentStatus === 'scheduled' && organization.paddleSubscriptionId ? <p className="rounded-xl bg-blue-50 p-4 text-sm font-semibold text-blue-900">Your first payment is processing. Access resumes when the subscription becomes active.</p> : <MsaPaymentButton organization={organization} email={currentUser.email} />)}
                {canManageAccount && !missing && !expiredTrial && loading && <div role="status" className="flex items-center gap-3 text-sm text-slate-500"><span className="h-5 w-5 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />Loading MSA details…</div>}
                {canManageAccount && !missing && !expiredTrial && quote && <>
                    <div className="grid gap-4 sm:grid-cols-3">
                        <div className="rounded-xl bg-slate-50 p-4"><p className="text-xs font-bold uppercase text-slate-400">Status</p><p className="mt-1 font-extrabold capitalize text-slate-900">{subscriptionStatusLabel(quote.status, organization.isTestOrganization)}</p></div>
                        <div className="rounded-xl bg-slate-50 p-4"><p className="text-xs font-bold uppercase text-slate-400">Access ended</p><p className="mt-1 font-extrabold text-slate-900">{date(quote.accessUntil || quote.trialEndsAt)}</p></div>
                        <div className="rounded-xl bg-slate-50 p-4"><p className="text-xs font-bold uppercase text-slate-400">Current annual price</p><p className="mt-1 font-extrabold text-slate-900">{money(quote.total, quote.currencyCode)}</p></div>
                    </div>
                    <div className="rounded-2xl border border-slate-200">
                        {quote.items.map(item => <div key={item.priceId} className="flex justify-between gap-4 border-b border-slate-100 px-5 py-3 text-sm last:border-0"><span>{item.name}{item.quantity > 1 ? ` × ${item.quantity}` : ''}</span><strong>{money(Number(item.amount) * item.quantity, quote.currencyCode)}</strong></div>)}
                    </div>
                    <button onClick={reactivate} disabled={processing} className="rounded-xl bg-blue-600 px-6 py-3 text-sm font-extrabold text-white shadow-lg shadow-blue-600/20 hover:bg-blue-700 disabled:opacity-50">{processing ? 'Opening checkout…' : 'Restart MSA Subscription'}</button>
                </>}
                {error && <p className="rounded-xl bg-red-50 p-4 text-sm font-semibold text-red-700">{error}</p>}
            </div>
        </div>
    </section>;
};

export default MsaAccessGate;
